<template>
    <div 
        class="system-card" 
        :style="cardStyle"
        :data-guid="system.GUID" 
        @mousedown="handleMouseDown" 
        @mouseup="handleMouseUp" 
        @mouseleave="handleMouseLeave"
    >
        <!-- 状态切换按钮 -->
        <div class="system-status-button">
            <button 
                class="action-btn edit-btn"
                :class="{ 'status-enabled': isStatusEnabled, 'status-disabled': !isStatusEnabled }"
                @click="handleStatusChange" 
                :disabled="system.isEditing"
            >
                {{ isStatusEnabled ? '已启用' : '已禁用' }}
            </button>
        </div>

        <!-- 顶部操作按钮（编辑/删除） -->
        <div class="card-actions">
            <button 
                class="action-btn edit-btn" 
                @click.stop="toggleEditMode" 
                :title="system.isEditing ? '保存' : '编辑'"
            >
                <EditOutlined v-if="!system.isEditing" />
                <CheckOutlined v-if="system.isEditing" />
           
            </button>
            <button 
                class="action-btn delete-btn" 
                @click.stop="handleDelete" 
                :title="`删除 ${system.name}`"
            >
                <DeleteOutlined />
            </button>
        </div>

        <!-- 卡片内容区 -->
        <div class="card-content">
            <!-- 系统大图标 -->
            <div class="system-icon" :style="{
                width: '100px',
                height: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'left',
                color: iconColor,
                fontSize: '64px',
                paddingTop: '50px',
                paddingLeft: '8px'
            }">
                <component :is="getSystemIcon()" @click="selectico()"/>
            </div>

            <!-- 系统名称输入框 -->
            <div>
                <a-input 
                    v-model:value="editName" 
                    placeholder="请输入系统名称" 
                    class="edit-name-input" 
                    :size="'small'"
                    :compact="true" 
                    :readonly="!system.isEditing" 
                    :class="{ 'input-readonly': !system.isEditing }" 
                    @dblclick.stop="toggleEditMode" 
                />
            </div>

            <!-- 系统地址输入框 -->
            <a-input 
                v-model:value="editUrl" 
                placeholder="请输入系统地址" 
                class="system-url" 
                :size="'small'" 
                :compact="true"
                :readonly="!system.isEditing" 
                :class="{ 'input-readonly2': !system.isEditing }" 
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, onUnmounted, computed } from 'vue'
import { defineProps, defineEmits } from 'vue'
import {
    EditOutlined,
    DeleteOutlined,
    CheckOutlined,
    PieChartOutlined,
    AlertOutlined,
    SettingOutlined,
    LaptopOutlined,
    QuestionOutlined,
    StarOutlined
} from '@ant-design/icons-vue'
import { message, Modal } from 'ant-design-vue'

// 卡片动态样式（同步父组件传递的宽高）
const cardStyle = computed(() => ({
    width: `${props.cardWidth}px`,
    height: `${props.cardHeight}px`
}));

// 定义Props
const props = defineProps({
    system: {
        type: Object,
        required: true,
        default: () => ({
            GUID: '',
            name: '',
            url: '',
            status: 'enabled',
            updateTime: '',
            isEditing: false,
            x: 0,
            y: 0,
        })
    },
    cardWidth: {
        type: Number,
        required: true
    },
    cardHeight: {
        type: Number,
        required: true
    },
    index: { 
        type: Number, 
        required: true 
    }
})

// 定义事件
const emit = defineEmits(['update-system', 'delete-system', 'update-position', 'update-long-press-status', 'update-status'])

// 核心状态
const editName = ref(props.system.name)
const editUrl = ref(props.system.url)
const showDragIcon = ref(false)
const longPressTimer = ref<number | null>(null)
const isReadyToDrag = ref(false)
const isDragging = ref(false)
const startDragX = ref(0)
const startDragY = ref(0)
const startCardX = ref(props.system.x)
const startCardY = ref(props.system.y)
const isLongPressing = ref(false);

// 同步卡片初始位置与数据
watch(
    () => props.system,
    (newVal) => {
        editName.value = newVal.name;
        editUrl.value = newVal.url;
        startCardX.value = newVal.x;
        startCardY.value = newVal.y;

        const cardEl = document.querySelector(`.system-card[data-guid="${newVal.GUID}"]`) as HTMLElement;
        if (cardEl) {
            cardEl.style.left = `${newVal.x}px`;
            cardEl.style.top = `${newVal.y}px`;
        }
    },
    { deep: true, immediate: true }
);
const selectico=(ico)=>{
    alert('我来了');
}

// 鼠标按下：区分长按激活拖拽与直接拖拽
const handleMouseDown = (e: MouseEvent) => {
    e.stopPropagation();
    
    // 编辑态或点击操作按钮时不响应
    if (props.system.isEditing) return;
    const isActionBtn = e.target?.closest('.card-actions') || e.target?.closest('.system-status-button');
    if (isActionBtn) return;

    // 标记长按状态并通知父组件
    isLongPressing.value = true;
    emit('update-long-press-status', isLongPressing.value);

    // 已激活拖拽：开始移动
    if (isReadyToDrag.value) {
        isDragging.value = true;
        showDragIcon.value = false;
        startDragX.value = e.clientX;
        startDragY.value = e.clientY;
        document.addEventListener('mousemove', handleDragMove);
        e.preventDefault();
        return;
    }

    // 未激活拖拽：启动长按定时器
    if (longPressTimer.value) clearTimeout(longPressTimer.value);
    longPressTimer.value = window.setTimeout(() => {
        showDragIcon.value = true;
        isReadyToDrag.value = true;
    }, 500);
};

// 鼠标松开：重置状态
const handleMouseUp = (e: MouseEvent) => {
    // 编辑态强制重置
    if (props.system.isEditing) {
        isLongPressing.value = false;
        isReadyToDrag.value = false;
        isDragging.value = false;
        showDragIcon.value = false;
        if (longPressTimer.value) {
            clearTimeout(longPressTimer.value);
            longPressTimer.value = null;
        }
        document.removeEventListener('mousemove', handleDragMove);
        return;
    }

    // 清除长按状态与定时器
    isLongPressing.value = false;
    emit('update-long-press-status', isLongPressing.value);
    if (longPressTimer.value) {
        clearTimeout(longPressTimer.value);
        longPressTimer.value = null;
    }

    // 停止拖拽
    if (isDragging.value) {
        isDragging.value = false;
        document.removeEventListener('mousemove', handleDragMove);
    }

    // 重置拖拽相关状态
    isReadyToDrag.value = false;
    showDragIcon.value = false;
    startDragX.value = 0;
    startDragY.value = 0;
};

// 拖拽移动：计算位置并更新
const handleDragMove = (e: MouseEvent) => {
    if (!isDragging.value) return;

    // 获取当前卡片DOM
    const cardEl = e.target?.closest('.system-card') as HTMLElement;
    if (!cardEl) return;

    // 计算父容器位置（定位基准）
    const parentEl = cardEl.parentElement;
    if (!parentEl) return;
    const parentRect = parentEl.getBoundingClientRect();

    // 计算移动距离与新位置（限制在父容器内）
    const moveX = e.clientX - startDragX.value;
    const moveY = e.clientY - startDragY.value;
    const newX = Math.max(0, Math.min(startCardX.value + moveX, parentRect.width - cardEl.offsetWidth));
    const newY = Math.max(0, Math.min(startCardY.value + moveY, parentRect.height - cardEl.offsetHeight));

    // 实时更新卡片样式与父组件数据
    cardEl.style.left = `${newX}px`;
    cardEl.style.top = `${newY}px`;
    emit('update-position', props.system.GUID, newX, newY);
};

// 鼠标离开：复用松开逻辑
const handleMouseLeave = (e: MouseEvent) => {
    if (props.system.isEditing) {
        isLongPressing.value = false;
        isReadyToDrag.value = false;
        isDragging.value = false;
        showDragIcon.value = false;
        if (longPressTimer.value) {
            clearTimeout(longPressTimer.value);
            longPressTimer.value = null;
        }
        document.removeEventListener('mousemove', handleDragMove);
        return;
    }
    isLongPressing.value = false;
    handleMouseUp(e);
};

// 匹配系统图标
const getSystemIcon = () => {
    const { name } = props.system;
    if (name.includes('质量管理')) return PieChartOutlined;
    if (name.includes('不良事件')) return AlertOutlined;
    if (name.includes('系统管理')) return SettingOutlined;
    if (name.includes('本地调试')) return LaptopOutlined;
    if (name.includes('科室')) return QuestionOutlined;
    return StarOutlined;
};

// 图标颜色
const iconColor = computed(() => '#1890ff');

// 切换编辑/保存模式
const toggleEditMode = () => {
    if (props.system.isEditing) {
        // 保存校验
        if (!editName.value.trim()) {
            message.warning('系统名称不能为空');
            return;
        }
        if (!editUrl.value.trim()) {
            message.warning('系统地址不能为空');
            return;
        }
        // 提交更新
        emit('update-system', props.system.GUID, {
            name: editName.value.trim(),
            url: editUrl.value.trim(),
            isEditing: false,
            updateTime: new Date().toLocaleDateString().replace(/\//g, '-')
        });
    } else {
        // 进入编辑模式
        emit('update-system', props.system.GUID, { isEditing: true });
    }
};

// 删除系统
const handleDelete = () => {
    Modal.confirm({
        title: '确认删除',
        content: `确定要删除系统「${props.system.name}」吗？删除后不可恢复`,
        okText: '确认',
        cancelText: '取消',
        onOk: () => {
            emit('delete-system', props.system.GUID);
            message.success('删除成功');
        }
    });
};

// 组件卸载：清理定时器与全局监听
onUnmounted(() => {
    if (longPressTimer.value) clearTimeout(longPressTimer.value);
    document.removeEventListener('mousemove', handleDragMove);
});

// 状态切换计算属性（enabled→true，disabled→false）
const isStatusEnabled = computed({
    get() {
        return props.system.status === 'enabled';
    },
    set() {} // 只读绑定，空实现
});

// 状态切换事件
const handleStatusChange = () => {
    const newStatus = isStatusEnabled.value ? 'disabled' : 'enabled';
    emit('update-status', props.system.GUID, newStatus);
    message.success(`已${newStatus === 'enabled' ? '启用' : '禁用'}`);
};
</script>

<style scoped>
/* 卡片基础样式 */
.system-card {
    background-color: #fff;
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    overflow: hidden;
    position: relative;
    transition: all 0.3s;
    cursor: default;
}

.system-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border-color: #d0d7dc;
    transform: translateY(-2px);
}

/* 拖拽提示图标样式 */
.drag-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 48px;
    color: rgba(24, 144, 255, 0.7);
    z-index: 20;
    pointer-events: none;
}

/* 拖拽状态光标 */
.system-card:has(.drag-icon) {
    cursor: grab;
}
.system-card:has(.drag-icon):active {
    cursor: grabbing;
}

/* 顶部操作按钮 */
.card-actions {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    gap: 8px;
    z-index: 10;
    opacity: 0;
    visibility: visible;
    transition: opacity 0.2s ease, visibility 0.2s ease;
}

.action-btn {
    height: 28px;
    border: none;
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.05);
    color: #666;
    cursor: pointer !important;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.action-btn:hover {
    background-color: rgba(0, 0, 0, 0.1);
}

.edit-btn:hover {
    color: #1890ff;
    background-color: rgba(24, 144, 255, 0.1);
}

.delete-btn:hover {
    color: #ff4d4f;
    background-color: rgba(255, 77, 79, 0.1);
}

/* 卡片内容区 */
.card-content {
    width: 100%;
    height: 100%;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    /* 核心：超浅色系垂直渐变，过渡范围拉宽，弱化边界感 */
    background: linear-gradient(
        180deg, 
        rgba(255, 255, 255, 0.98) 0%,  /* 顶部：近乎纯白，保留通透感 */
        rgba(254, 254, 255, 0.95) 60%, /* 中部：极浅过渡，视觉无断层 */
        rgba(252, 253, 254, 0.92) 100% /* 底部：微暗收尾，仅添一丝层次 */
    );
    /* 弱化内阴影：用极淡的灰度阴影，模拟“空气感”凹陷 */
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.6),  /* 顶部亮边：融入背景，不突兀 */
                inset 0 -1px 2px rgba(220, 224, 228, 0.3); /* 底部暗边：低透明度，仅显轮廓 */
}

.system-icon {
    /* width: 150px;
    height: 150px; */
    display: flex;
    align-items: center;
    justify-content: left;
    font-size: 64px;
    opacity: .7;
}

/* 名称输入框样式 */
.edit-name-input {
    position: absolute;
    left: 110px;
    top: 64px;
    width: 190px;
    font-size: 16px;
    font-weight: 500;
}

/* 地址输入框样式 */
.system-url {
    position: absolute;
    left: 110px;
    top: 92px;
    width: 190px;
    font-size: 14px;
    font-weight: 500;
    color: #333 !important;
}

/* 非编辑态输入框样式（完全隐藏边框，模拟纯文本） */
.input-readonly,
.input-readonly2 {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    cursor: default !important;
    padding: 0 !important; /* 移除内边距，完全贴合文本 */
    margin: 0 !important;
    height: auto !important; /* 高度自适应内容 */
}

.input-readonly {
    color: #333 !important; /* 名称文本颜色 */
}

.input-readonly2 {
    color: #999 !important; /* 地址文本颜色 */
}

/* 编辑态输入框样式（显示完整边框） */
:deep(.edit-name-input:not(.input-readonly).ant-input),
:deep(.system-url:not(.input-readonly2).ant-input) {
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
    border-bottom: 1px solid #d9d9d9 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    z-index: inherit;
}

/* 编辑态聚焦样式 */
:deep(.edit-name-input:not(.input-readonly).ant-input:focus),
:deep(.system-url:not(.input-readonly2).ant-input:focus) {
    /* border-color: #1890ff !important; 
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;  */
    border-color: #1890ff !important; 
}

.input-readonly::-moz-selection,
.input-readonly::selection {
    background: transparent;
}

/* Ant Design 组件样式调整 */
:deep(.anticon) {
    display: flex;
    align-items: center;
    justify-content: center;
}

:deep(.ant-input) {
    border-radius: 4px !important;
}

/*  hover 显示操作按钮 */
.system-card:hover .card-actions {
    opacity: 1;
    visibility: visible;
}

/* 状态按钮容器 */
.system-status-button {
    position: absolute;
    left: 10px;
    top: 10px;
    display: flex;
    z-index: 10;
}

/* 状态按钮样式 */
.status-enabled {
    background-color: #f0f9eb;
    color: #52c41a;
}

.status-enabled:hover {
    background-color: #e6f7e0;
}

.status-disabled {
    background-color: #f5f5f5;
    color: #999;
}

.status-disabled:hover {
    background-color: #eeeeee;
}

/* 编辑模式卡片样式 */
.system-card:has(.edit-name-input:not(.input-readonly)) {
    opacity: 0.95;
    cursor: text !important;
}
</style>
    